import { query, requests } from '@kurtosys/ksys-api-client';
import { common } from '@kurtosys/ksys-app-template';
import { IEndpointsConfiguration, IRequestOptions } from '@kurtosys/types/api/common/index.js';
import { IManifest } from '@kurtosys/types/appsManager/IManifest.js';
import { action, computed, makeObservable, observable } from 'mobx';

import { IComponentStylesBase } from '../models/IComponentStylesBase.js';
import { IConfigurationBase } from '../models/IConfigurationBase.js';

import { AppStoreBase } from './base/AppStoreBase.js';
import { StoreBase } from './base/StoreBase.js';
import { StoreContext } from './StoreContext.js';

type IQueryContextClient = query.IQueryContextClient;
type TListTranslations = requests.config.ListTranslations;
type TGetApplicationClientConfiguration = requests.applicationManager.GetApplicationClientConfiguration;
type TGetTheme = requests.applicationManager.GetTheme;
type TGetUserByToken = requests.auth.GetUserByToken;
type TGetApplicationAppConfig = requests.root.GetApplicationAppConfig;
type TGetFilters = requests.fund.GetFilters;
type KsysRequest<TRequestBody, TResponseBody> = requests.KsysRequest<TRequestBody, TResponseBody>;

const GetApplicationAppConfig = requests.root.GetApplicationAppConfig;
const GetApplicationClientConfiguration = requests.applicationManager.GetApplicationClientConfiguration;
const GetUserByToken = requests.auth.GetUserByToken;
const ListCommentaries = requests.config.ListCommentaries;
const ListDisclaimers = requests.config.ListDisclaimers;
const ListTranslations = requests.config.ListTranslations;
const RetrieveClientConfiguration = requests.config.RetrieveClientConfiguration;
const RetrieveUserConfiguration = requests.config.RetrieveUserConfiguration;
const Execute = requests.dataset.Execute;
const ExecuteGet = requests.dataset.ExecuteGet;
const Retrieve = requests.documents.Retrieve;
const RetrieveDocument = requests.documents.RetrieveDocument;
const RetrieveDocumentZip = requests.documents.RetrieveDocumentZip;
const SearchDocuments = requests.documents.SearchDocuments;
const SavedSearchEntity = requests.fund.SavedSearchEntity;
const SearchEntity = requests.fund.SearchEntity;
const SearchGroupedEntity = requests.fund.SearchGroupedEntity;
const GetTheme = requests.applicationManager.GetTheme;
const GetFilters = requests.fund.GetFilters;

export class KurtosysApiStore<
	TConfiguration extends IConfigurationBase,
	TComponentStyles extends IComponentStylesBase,
> extends StoreBase<TConfiguration, TComponentStyles> {
	@observable token = '';

	constructor(storeContext: StoreContext<TConfiguration, TComponentStyles, any, any, any>, manifest: IManifest) {
		super(storeContext, manifest);

		makeObservable(this);
	}

	@computed
	get client(): IQueryContextClient {
		const response: IQueryContextClient = {
			config: {
				clientConfiguration: {
					retrieve: this.createEndpoint(RetrieveClientConfiguration),
				},
				userConfiguration: {
					retrieve: this.createEndpoint(RetrieveUserConfiguration),
				},
			},
			commentaries: {
				list: this.createEndpoint(ListCommentaries),
			},
			disclaimers: {
				list: this.createEndpoint(ListDisclaimers),
			},
			documents: {
				search: this.createEndpoint(SearchDocuments),
				retrieve: this.createEndpoint(RetrieveDocument),
				retrieveByProperty: this.createEndpoint(Retrieve),
				retrieveZip: this.createEndpoint(RetrieveDocumentZip),
			},
			entity: {
				search: this.createEndpoint(SearchEntity),
				searchGrouped: this.createEndpoint(SearchGroupedEntity),
				savedSearchEntity: this.createEndpoint(SavedSearchEntity),
			},
			auth: {
				getUserByToken: this.createEndpoint(GetUserByToken),
			},
			datasets: {
				execute: this.createEndpoint(Execute),
				executeGet: this.createEndpoint(ExecuteGet),
			},
			options: {
				baseUrl: this.serviceUrl,
			},
		};
		return response;
	}

	@computed
	get endpointsConfiguration(): IEndpointsConfiguration {
		const appStore = this.storeContext.get<AppStoreBase<TConfiguration, TComponentStyles>>('appStore');
		return (appStore && appStore.coreConfigurations && appStore.coreConfigurations.endpointsConfiguration) || {};
	}

	@computed
	get wrappedToken(): { value: string } {
		return { value: this.token };
	}

	get serviceUrl(): string {
		const appStore = this.storeContext.get<AppStoreBase<TConfiguration, TComponentStyles>>('appStore');
		if (
			appStore.authentication &&
			appStore.authentication.serviceUrl &&
			appStore.authentication.serviceUrl.length > 0
		) {
			return appStore.authentication.serviceUrl;
		}
		if (appStore.configuration && appStore.configuration.core && appStore.configuration.core.serviceUrl) {
			return appStore.configuration.core.serviceUrl;
		}
		return this.getBaseAddress();
	}

	get wordpressUrl(): string {
		const appStore = this.storeContext.get<AppStoreBase<TConfiguration, TComponentStyles>>('appStore');
		if (appStore.configuration && appStore.configuration.core && appStore.configuration.core.wordpressUrl) {
			return appStore.configuration.core.wordpressUrl;
		}
		return '/';
	}

	@computed
	get options(): IRequestOptions<any> {
		return {
			baseUrl: this.serviceUrl,
			baseWordpressUrl: this.wordpressUrl,
		};
	}

	@computed
	get proxyOptions(): IRequestOptions<any> {
		return {
			baseUrl: this.serviceUrl,
			disableCache: true,
			disableCachableRequests: true,
		};
	}

	@action
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	async initialize(): Promise<void> {}

	getBaseAddress(suffix = 'services/') {
		const appStore = this.storeContext.get<AppStoreBase<TConfiguration, TComponentStyles>>('appStore');
		return this.manifest.getBaseUrl(suffix, appStore.appParamsHelper);
	}

	createEndpoint<TRequestOptionsBody = any>(
		Endpoint: TEndpointClass,
		options: ICreateEndpointOptions<TRequestOptionsBody> = {},
	) {
		const { isProxy, optionsOverride } = options;
		let endpointOptions = isProxy ? this.proxyOptions : this.options;
		if (optionsOverride) {
			endpointOptions = common.commonUtils.deepMergeObjects(endpointOptions, optionsOverride);
		}
		return new Endpoint(this.wrappedToken, endpointOptions, this.endpointsConfiguration);
	}

	@computed
	get listTranslations(): TListTranslations {
		return this.createEndpoint(ListTranslations);
	}

	@computed
	get getApplicationClientConfiguration(): TGetApplicationClientConfiguration {
		return this.createEndpoint(GetApplicationClientConfiguration);
	}

	@computed
	get getAppConfig(): TGetApplicationAppConfig {
		return this.createEndpoint(GetApplicationAppConfig);
	}

	@computed
	get getTheme(): TGetTheme {
		return this.createEndpoint(GetTheme);
	}

	@computed
	get getUserByToken(): TGetUserByToken {
		return this.createEndpoint(GetUserByToken);
	}

	@computed
	get getFilters(): TGetFilters {
		return this.createEndpoint(GetFilters);
	}
}

type TEndpointClass = new (
	token: TToken,
	options: IRequestOptions<any>,
	endpointsConfiguration?: IEndpointsConfiguration,
) => KsysRequest<any, any>;

type TToken = { value: string } | undefined;

interface ICreateEndpointOptions<TRequestOptionsBody = any> {
	isProxy?: boolean;
	optionsOverride?: Partial<IRequestOptions<TRequestOptionsBody>>;
}
